import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModal } from "../../redux/modalSlice";
import { useRequest } from "../../hooks/UseRequest";
import Loading from "../../components/Loading";
import { FormatISODateTime, FormatISOTime, formatDuration } from "../../helpers/formatting";

export default function TranscriptNoteModal() {
  const modal = useSelector((state) => state.modal);
  const note = modal.options?.note;
  const dispatch = useDispatch();
  const request = useRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [callData, setCallData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleClose = () => {
    dispatch(setModal({ is_active: false }));
  };

  useEffect(() => {
    if (modal.is_active && modal.type === "transcript_note_modal" && note?.id) {
      getTranscriptNote();
    } else {
      setCallData(null);
    }
  }, [modal.is_active, modal.type, note?.id]);

  const getTranscriptNote = async () => {
    setIsLoading(true);
    setErrorMsg("");
    request.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/customer_note/${note.id}/transcript_data`
    );
    try {
      const response = await request.get();
      const raw = response?.data?.data;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      setCallData(parsed ?? null);
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        error?.response?.data?.messageExtreme ??
        "Failed to load call data.";
      setErrorMsg(message);
      setCallData(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (!modal.is_active || modal.type !== "transcript_note_modal") return null;

  return (
    <div className="modal is-active">
      <div className="modal-background" />
      <div className="modal-card" style={{ width: 1100, maxWidth: "95vw" }}>
        <header className="modal-card-head py-3">
          <p className="modal-card-title">Dialer Transcript Note</p>
          <button
            className="delete"
            aria-label="close"
            onClick={handleClose}
            type="button"
          />
        </header>

        <section className="modal-card-body">
          <div id="caller_data_customer_note">
            {isLoading && <Loading />}
            {!isLoading && errorMsg && (
              <p className="has-text-danger">{errorMsg}</p>
            )}
            {!isLoading && callData && <CallDataView data={callData} />}
            {!isLoading && !errorMsg && !callData && (
              <p className="has-text-grey">No call data.</p>
            )}
          </div>
        </section>

        <footer className="modal-card-foot is-justify-content-end py-2 pr-2">
          <div className="lox-form__buttons-wrapper has-text-right">
            <button
              type="button"
              className="button is-small has-background-grey has-text-white"
              onClick={handleClose}
            >
              Done
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div className="tile">
      <p className="label">{label}</p>
      <p className="value">{value || "—"}</p>
    </div>
  );
}

function groupEntries(entries) {
  const finals = entries.filter((e) => e.is_final);
  return finals.reduce((acc, entry) => {
    const last = acc[acc.length - 1];
    if (last && last.role === entry.role && entry.role !== "SYSTEM") {
      last.text = last.text + " " + entry.text;
      last.timestamp = entry.timestamp;
    } else {
      acc.push({ ...entry });
    }
    return acc;
  }, []);
}

/** ---------- main content view ---------- */
function CallDataView({ data }) {
  const grouped = useMemo(() => {
    if (!Array.isArray(data?.entries) || !data.entries.length) return [];
    return groupEntries(data.entries);
  }, [data]);

  return (
    <div className="content">
      <div className="card-block">
        <div className="row">
          {data.call_id ? (
            <span className="badge" title="Call ID">{data.call_id}</span>
          ) : ""}
          {data.transfer_log_id != null ? (
            <a
              href={`/rc_transfer_log/${data.transfer_log_id}`}
              className="badge transfer_log_id"
              title="Transfer Log Id"
              target="_blank"
              rel="noopener noreferrer"
            >
              Transfer Log ID: #{data.transfer_log_id}
            </a>
          ) : ""}
        </div>
        <div className="grid mt-3">
          <SummaryItem label="Start" value={FormatISODateTime(data.call_started)} />
          <SummaryItem label="End" value={FormatISODateTime(data.call_ended)} />
          <SummaryItem label="Duration" value={formatDuration(data.duration_seconds)} />
          <SummaryItem label="Total Entries" value={data.total_entries != null ? String(data.total_entries) : "—"} />
        </div>
      </div>

      <div className="card-block">
        <div className="title is-6 mb-2">Transcript</div>
        {grouped.length === 0 ? (
          <p className="has-text-grey">No transcript entries.</p>
        ) : (
          <div style={{ maxHeight: 480, overflowY: "auto", padding: "0.25rem 0.5rem" }}>
            {grouped.map((entry) => {
              const key = `${entry.role}-${entry.timestamp}`;
              if (entry.role === "SYSTEM") {
                return (
                  <div key={key} className="has-text-centered my-2">
                    <span className="tag is-light is-small has-text-grey">
                      {entry.text}
                    </span>
                  </div>
                );
              }

              const isUser = entry.role === "USER";
              return (
                <div
                  key={key}
                  className={`is-flex my-1 ${isUser ? "is-justify-content-flex-end" : "is-justify-content-flex-start"}`}
                >
                  <div style={{ maxWidth: "65%" }}>
                    <p
                      className={`is-size-7 has-text-grey mb-1 ${isUser ? "has-text-right" : ""}`}
                    >
                      {entry.role} · {FormatISOTime(entry.timestamp)}
                    </p>
                    <div
                      className={isUser ? "has-text-white" : "has-background-light has-text-dark"}
                      style={{
                        borderRadius: 8,
                        padding: "0.5rem 0.75rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.4,
                        backgroundColor: isUser ? "#2274a5" : undefined,
                      }}
                    >
                      {entry.text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
